import { computed, effectScope, reactive, watch } from 'vue';

import { analyticsService } from '../services/AnalyticsService';

// Toast timeouts have been a recurring UX problem (Vuetify pauses its own
// timer on hover/focus, so on touch devices a snackbar can stick around far
// longer than intended). The composable owns the timer outright now and the
// `<v-snackbar>` runs with `:timeout="-1"`, so this is the single source of
// truth for "how long can a notification stay on screen".
export const DEFAULT_DURATION_MS = 2_500;
export const MAX_DURATION_MS = 6_000;

const notification = reactive<NotificationData>({
    hasNotification: false,
    message: undefined,
    type: undefined,
    duration: undefined,
    confirmAction: undefined,
    cancelAction: undefined,
});

const queue: NotificationPayload[] = reactive([]);

// Timer + correlation state for the currently visible notification. Module
// scoped so a programmatic `clearAllNotifications` from one component can stop
// the timer started by another.
let timeoutId: ReturnType<typeof setTimeout> | null = null;
let currentId: string | null = null;
let shownAt: number | null = null;

// Auto-advance the queue when the active notification closes. Scoped so it
// survives the module lifetime without being attached to a component.
const scope = effectScope(true);
scope.run(() => {
    watch(
        () => notification.hasNotification,
        (isVisible) => {
            if (!isVisible) {
                const next = queue.shift();
                if (next != null) {
                    applyNotification(next);
                }
            }
        },
        { flush: 'sync' },
    );
});

function clampDuration(requested: number | undefined): number {
    const value = requested ?? DEFAULT_DURATION_MS;
    if (!Number.isFinite(value) || value <= 0) {
        return DEFAULT_DURATION_MS;
    }
    return Math.min(value, MAX_DURATION_MS);
}

function applyNotification(payload: NotificationPayload) {
    clearTimer();

    notification.message = payload.message;
    notification.type = payload.type;
    notification.confirmAction = payload.confirmAction;
    notification.cancelAction = payload.cancelAction;
    notification.duration = clampDuration(payload.duration);
    notification.hasNotification = true;

    currentId = mintId();
    shownAt = Date.now();
    trackImpression();

    timeoutId = setTimeout(() => {
        timeoutId = null;
        dismiss('expired');
    }, notification.duration);
}

function clearTimer(): void {
    if (timeoutId != null) {
        clearTimeout(timeoutId);
        timeoutId = null;
    }
}

function mintId(): string {
    try {
        return crypto.randomUUID();
    } catch {
        // `crypto.randomUUID` throws in insecure contexts. Analytics
        // correlation is best-effort — fall back to a coarse id.
        return `notif-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
}

function impressionProperties(): Record<string, unknown> {
    return {
        notification_id: currentId,
        type: notification.type,
        has_confirm_action: notification.confirmAction != null,
        has_cancel_action: notification.cancelAction != null,
        duration_ms: notification.duration,
    };
}

function trackImpression(): void {
    try {
        analyticsService.track({
            eventName: 'notification_shown',
            eventType: 'impression',
            properties: impressionProperties(),
        });
    } catch {
        // analyticsService swallows internally; the catch is defence-in-depth
        // in case it ever changes. A failed impression must not break the UI.
    }
}

function trackDismiss(reason: DismissReason): void {
    try {
        analyticsService.track({
            eventName: 'notification_dismissed',
            eventType: 'interaction',
            properties: {
                ...impressionProperties(),
                dismiss_reason: reason,
                visible_duration_ms: shownAt != null ? Date.now() - shownAt : null,
            },
        });
    } catch {
        // See trackImpression.
    }
}

function dismiss(reason: DismissReason): void {
    if (!notification.hasNotification) {
        return;
    }
    trackDismiss(reason);
    clearTimer();
    currentId = null;
    shownAt = null;
    notification.hasNotification = false;
}

export function useNotifications() {
    function showNotification(payload: NotificationPayload) {
        if (notification.hasNotification) {
            queue.push(payload);
            return;
        }
        applyNotification(payload);
    }

    function showError(message: string, action?: Action) {
        showNotification({
            message,
            confirmAction: action,
            type: 'error',
        });
    }

    function showSuccess(message: string, action?: Action) {
        showNotification({
            message,
            confirmAction: action,
            type: 'success',
        });
    }

    function showInfo(message: string, action?: Action) {
        showNotification({
            message,
            confirmAction: action,
            type: 'info',
        });
    }

    function hideNotification() {
        dismiss('programmatic');
    }

    function clearAllNotifications() {
        queue.splice(0, queue.length);
        dismiss('programmatic');
    }

    const notificationColor = computed(() => {
        if (!notification.type) return '';

        switch (notification.type) {
            case 'info':
                return 'light-blue-lighten-4';
            case 'error':
                return 'error';
            case 'success':
                return 'green-lighten-2';
        }
    });

    return {
        notification,
        notificationColor,
        showNotification,
        showError,
        showSuccess,
        showInfo,
        hideNotification,
        clearAllNotifications,
        dismiss,
    };
}

type NotificationType = 'info' | 'error' | 'success';

// 'programmatic' covers in-code dismissals (clearAllNotifications, store reset)
// that aren't a direct user action; the action-button reasons keep the
// confirm/cancel paths distinguishable from a plain X tap.
export type DismissReason =
    | 'manual'
    | 'expired'
    | 'confirm_action'
    | 'cancel_action'
    | 'programmatic';

type Action = {
    label: string;
    callback: () => void;
};

type NotificationPayload = {
    message: string;
    type: NotificationType;
    confirmAction?: Action;
    cancelAction?: Action;
    duration?: number;
};

type NotificationData = Partial<NotificationPayload> & {
    hasNotification: boolean;
};
